import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { userAPI } from '../../services/api';
import { Loader2, Check, User, Bell, Palette } from 'lucide-react';
import Alert from '../../components/common/Alert';
import ScodeCustomizationComponent from './components/scodeCustomization';

export default function Settings() {
  const { user, updateUserProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingScode, setIsSavingScode] = useState(false);
  const [success, setSuccess] = useState(false);
  const [scodeSuccess, setScodeSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phoneNumber: user?.phoneNumber || '',
    jobTitle: user?.jobTitle || '',
    department: user?.department || '',
    timezone: user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    emailNotifications: user?.emailNotifications || { updates: false },
    scodeSettings: user?.profileSettings?.scodeSettings || {
      theme: 'default',
      bgColor: '#2a2a2a',
      accentColor: '#10B981',
      textColor: '#ffffff',
      badgeColor: '#4a4a4a',
      style: 'standard'
    }
  });

  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const response = await userAPI.getProfile();
        const profileData = response.data;
        
        // Ensure we have default scode settings if none exist
        const scodeSettings = profileData.profileSettings?.scodeSettings || {
          theme: 'default',
          bgColor: '#2a2a2a',
          accentColor: '#10B981',
          textColor: '#ffffff',
          badgeColor: '#4a4a4a',
          style: 'standard'
        };
        
        setFormData(prev => ({
          ...prev,
          ...profileData,
          emailNotifications: profileData.emailNotifications || { updates: false },
          scodeSettings
        }));
      } catch (err) {
        console.error('Error loading profile:', err);
        setError('Failed to load profile data. Please refresh the page.');
      }
    };

    loadUserProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleScodeSettingsChange = (newSettings) => {
    setFormData(prev => ({
      ...prev,
      scodeSettings: newSettings
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Use the existing updateProfile endpoint with all data
      const response = await userAPI.updateProfile({
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber,
        jobTitle: formData.jobTitle,
        department: formData.department,
        timezone: formData.timezone,
        emailNotifications: formData.emailNotifications,
        profileSettings: {
          ...user?.profileSettings,
          scodeSettings: formData.scodeSettings
        }
      });

      if (updateUserProfile) {
        await updateUserProfile(response.data);
      }
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSaveScodeSettings = async () => {
    setIsSavingScode(true);
    setError(null);
    setScodeSuccess(false);
    
    try {
      // Update just the scode settings part of the profile
      const response = await userAPI.updateProfile({
        profileSettings: {
          ...user?.profileSettings,
          scodeSettings: formData.scodeSettings
        }
      });

      if (updateUserProfile) {
        await updateUserProfile(response.data);
      }
      
      setScodeSuccess(true);
      setTimeout(() => setScodeSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save scode settings');
    } finally {
      setIsSavingScode(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: <User className="h-4 w-4" /> },
    { id: 'notifications', label: 'Notifications', icon: <Bell className="h-4 w-4" /> },
    { id: 'appearance', label: 'Email Appearance', icon: <Palette className="h-4 w-4" /> }
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-midnight font-heading">Settings</h1>
      
      {/* Tabs */}
      <div className="border-b border-ebony border-opacity-20">
        <nav className="-mb-px flex space-x-8">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                ${activeTab === tab.id
                  ? 'border-violet text-violet'
                  : 'border-transparent text-ebony hover:text-midnight hover:border-ebony hover:border-opacity-30'}
              `}
            >
              <div className="flex items-center gap-2">
                {tab.icon}
                {tab.label}
              </div>
            </button>
          ))}
        </nav>
      </div>
      
      <div className="bg-white shadow-md rounded-xl">
        {error && (
          <div className="px-4 py-5 sm:p-6">
            <Alert type="error" title="Error">
              {error}
            </Alert>
          </div>
        )}
        
        {activeTab === 'profile' && (
          <form onSubmit={handleSubmit} className="px-4 py-5 sm:p-6">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-midnight font-heading">Profile Settings</h3>
                <p className="mt-2 text-sm text-ebony">
                  Manage your account information and preferences.
                </p>
              </div>

              {success && (
                <Alert type="success" title="Profile updated successfully">
                  Your changes have been saved.
                </Alert>
              )}

              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gunmetal">
                    First Name
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className="shadow-sm focus:ring-sky-blue focus:border-sky-blue block w-full sm:text-sm border-ebony border-opacity-50 rounded-pill"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gunmetal">
                    Last Name
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className="shadow-sm focus:ring-sky-blue focus:border-sky-blue block w-full sm:text-sm border-ebony border-opacity-50 rounded-pill"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gunmetal">
                    Email
                  </label>
                  <div className="mt-1">
                    <input
                      type="email"
                      value={user?.email || ''}
                      readOnly
                      className="bg-old-lace bg-opacity-50 shadow-sm block w-full sm:text-sm border-ebony border-opacity-30 rounded-pill cursor-not-allowed"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gunmetal">
                    Phone Number
                  </label>
                  <div className="mt-1">
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      className="shadow-sm focus:ring-sky-blue focus:border-sky-blue block w-full sm:text-sm border-ebony border-opacity-50 rounded-pill"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gunmetal">
                    Job Title
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      name="jobTitle"
                      value={formData.jobTitle}
                      onChange={handleChange}
                      className="shadow-sm focus:ring-sky-blue focus:border-sky-blue block w-full sm:text-sm border-ebony border-opacity-50 rounded-pill"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gunmetal">
                    Department
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      name="department"
                      value={formData.department}
                      onChange={handleChange}
                      className="shadow-sm focus:ring-sky-blue focus:border-sky-blue block w-full sm:text-sm border-ebony border-opacity-50 rounded-pill"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <button
                type="submit"
                disabled={isLoading}
                className="inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-pill shadow-md text-white bg-violet hover:bg-violet/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet disabled:opacity-50 transition-colors"
              >
                {isLoading ? (
                  <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                ) : success ? (
                  <Check className="-ml-1 mr-2 h-4 w-4" />
                ) : null}
                {isLoading ? 'Saving...' : success ? 'Saved!' : 'Save Changes'}
              </button>
            </div>
          </form>
        )}
        
        {activeTab === 'notifications' && (
          <form onSubmit={handleSubmit} className="px-4 py-5 sm:p-6">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-midnight font-heading">Notification Settings</h3>
                <p className="mt-2 text-sm text-ebony">
                  Manage how and when you receive notifications.
                </p>
              </div>

              {success && (
                <Alert type="success" title="Settings updated successfully">
                  Your notification preferences have been saved.
                </Alert>
              )}

              <div>
                <h4 className="text-sm font-medium text-midnight">Email Notifications</h4>
                <div className="mt-4 space-y-4">
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="updates"
                        name="emailNotifications.updates"
                        type="checkbox"
                        checked={formData.emailNotifications.updates}
                        onChange={handleChange}
                        className="focus:ring-violet h-4 w-4 text-violet border-ebony border-opacity-50 rounded"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="updates" className="font-medium text-gunmetal">
                        Product Updates
                      </label>
                      <p className="text-ebony">Receive emails about product updates and new features.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <button
                type="submit"
                disabled={isLoading}
                className="inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-pill shadow-md text-white bg-violet hover:bg-violet/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet disabled:opacity-50 transition-colors"
              >
                {isLoading ? (
                  <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                ) : success ? (
                  <Check className="-ml-1 mr-2 h-4 w-4" />
                ) : null}
                {isLoading ? 'Saving...' : success ? 'Saved!' : 'Save Changes'}
              </button>
            </div>
          </form>
        )}
        
        {activeTab === 'appearance' && (
          <div className="px-4 py-5 sm:p-6">
            <div className="relative">
              {error && (
                <Alert type="error">
                  {error}
                </Alert>
              )}
              
              <ScodeCustomizationComponent
                scodeSettings={formData.scodeSettings || {
                  theme: 'default',
                  bgColor: '#2a2a2a',
                  accentColor: '#10B981',
                  textColor: '#ffffff',
                  badgeColor: '#4a4a4a',
                  style: 'standard'
                }}
                onChange={handleScodeSettingsChange}
                onSave={handleSaveScodeSettings}
                isSaving={isSavingScode}
                saveSuccess={scodeSuccess}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}